/**
 * Triggers paragraph re-sync at the natural moments the user actually looks at
 * the add-in panel — without any background polling.
 *
 * Why no polling / no DocumentSelectionChanged:
 *   Both approaches cost a Word.run on a recurring basis. On large documents
 *   even `body.paragraphs.load('items')` + `context.sync()` is not free — Word
 *   has to serialize every paragraph proxy across the host↔add-in boundary,
 *   which causes perceptible micro-stalls during typing.
 *
 * What we do instead:
 *   Fire a single "maybe-changed" notification whenever the add-in window
 *   regains focus, becomes visible, or receives a pointerenter on the panel
 *   root. These are the exact moments the user turns their attention back to
 *   the cards from editing the document, so syncing then is both timely and
 *   completely invisible cost-wise (zero background work).
 *
 *   A throttle (MIN_INTERVAL_MS) prevents bursts of focus events from firing
 *   the subscriber more than once per short window.
 *
 * Subscribers should perform their own actual diff (e.g. whether any clause's
 * stored paragraph index is now stale) — this watcher is a pure "user is
 * looking at the panel" trigger.
 */

type AttentionCallback = () => void;

/** Minimum time between two notifications. Prevents focus-bounce storms. */
const MIN_INTERVAL_MS = 1500;

function isBrowserEnv(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

export class DocumentStructureWatcher {
  private static _instance: DocumentStructureWatcher | null = null;

  static getInstance(): DocumentStructureWatcher {
    if (!DocumentStructureWatcher._instance) {
      DocumentStructureWatcher._instance = new DocumentStructureWatcher();
    }
    return DocumentStructureWatcher._instance;
  }

  private subscribers: Set<AttentionCallback> = new Set();
  private started = false;
  private lastFireMs = 0;
  private boundOnFocus: (() => void) | null = null;
  private boundOnVisibility: (() => void) | null = null;

  /**
   * Subscribe to "user is now looking at the panel" notifications.
   * Returns an unsubscribe function. Listeners auto-attach on first
   * subscriber and detach when the last one unsubscribes.
   */
  subscribe(callback: AttentionCallback): () => void {
    this.subscribers.add(callback);
    this._maybeStart();
    return () => {
      this.subscribers.delete(callback);
      if (this.subscribers.size === 0) {
        this._stop();
      }
    };
  }

  /**
   * Manually request a notification on the next tick. Useful for triggers
   * that subscribers know about (e.g. card hover) without re-implementing
   * the throttle.
   */
  notifyNow(): void {
    this._fire();
  }

  private _maybeStart(): void {
    if (this.started || !isBrowserEnv()) return;
    this.started = true;

    this.boundOnFocus = () => this._fire();
    this.boundOnVisibility = () => {
      if (document.visibilityState === 'visible') this._fire();
    };

    window.addEventListener('focus', this.boundOnFocus);
    document.addEventListener('visibilitychange', this.boundOnVisibility);
  }

  private _stop(): void {
    if (!this.started || !isBrowserEnv()) return;
    this.started = false;
    if (this.boundOnFocus) {
      window.removeEventListener('focus', this.boundOnFocus);
      this.boundOnFocus = null;
    }
    if (this.boundOnVisibility) {
      document.removeEventListener('visibilitychange', this.boundOnVisibility);
      this.boundOnVisibility = null;
    }
  }

  private _fire(): void {
    const now = Date.now();
    if (now - this.lastFireMs < MIN_INTERVAL_MS) return;
    this.lastFireMs = now;

    this.subscribers.forEach((cb) => {
      try {
        cb();
      } catch {
        // ignore subscriber errors
      }
    });
  }
}
